import { useEffect, useState } from 'react'
import type { ZodError } from 'zod'
import { loadDemoConditions } from '../demo-data/conditions';
import { loadDemoSymptoms } from '../demo-data/symptoms';
import { loadDemoMedications } from '../demo-data/medications';
import { loadDemoFoodEntries } from '../demo-data/food';
import { loadDemoDeviceData } from '../demo-data/deviceData';
import { loadJSON, saveJSON, removeItem } from '../utils/localStorageHelpers';
import {
  ConditionEntry,
  NewConditionSchema,
  NewSymptomSchema,
  SymptomEntry,
  NewMedicationSchema,
  MedicationEntry,
  NewFoodEntrySchema,
  FoodEntry,
  DeviceMetric,
  NewDeviceMetricSchema,
  NewCondition,
  NewSymptom,
  NewMedication,
  NewFoodEntry
} from '../types/records';

const demoMode =
  import.meta.env.VITE_DEMO_MODE === 'true' ||
  (typeof window !== 'undefined' && localStorage.getItem('demoMode') === 'true')

function loadValidatedArray<T>(
  key: string,
  demoLoader: () => T[],
  schema: { array: () => { safeParse: (data: unknown) => { success: boolean } } }
): T[] {
  if (!demoMode) return [];
  const stored = loadJSON<T[]>(key);
  if (stored && schema.array().safeParse(stored).success) {
    return stored;
  }
  if (stored) {
    removeItem(key);
  }
  const demo = demoLoader();
  return schema.array().safeParse(demo).success ? demo : [];
}

// Mock data hooks to replace Supabase functionality
// Medical Conditions Hook
export function useMedicalConditions() {
  const initial = loadValidatedArray<ConditionEntry>(
    'demo_conditions',
    loadDemoConditions,
    NewConditionSchema
  );
  const [conditions, setConditions] = useState<ConditionEntry[]>(initial);
  const [loading] = useState(false)
  const [error, setError] = useState<ReturnType<ZodError['format']> | null>(null)

  useEffect(() => {
    if (demoMode) {
      saveJSON('demo_conditions', conditions)
    }
  }, [conditions])

  const addCondition = async (
    conditionData: NewCondition
  ): Promise<ConditionEntry | null> => {
    setError(null)
    const parsed = NewConditionSchema.safeParse(conditionData)
    if (!parsed.success) {
      const formatted = parsed.error.format()
      setError(formatted)
      return null
    }
    const newCondition: ConditionEntry = {
      id: Date.now().toString(),
      user_id: 'demo-user-id',
      ...parsed.data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    setConditions(prev => [...prev, newCondition])
    return newCondition
  }

  return {
    conditions,
    loading,
    error,
    addCondition,
    refetch: () => {
      if (demoMode) {
        setConditions(
          loadValidatedArray<ConditionEntry>(
            'demo_conditions',
            loadDemoConditions,
            NewConditionSchema
          )
        )
      }
    }
  }
}

// Symptoms Hook
export function useSymptoms() {
  const initial = loadValidatedArray<SymptomEntry>(
    'demo_symptoms',
    loadDemoSymptoms,
    NewSymptomSchema
  );
  const [symptoms, setSymptoms] = useState<SymptomEntry[]>(initial);
  const [loading] = useState(false)
  const [error, setError] = useState<ReturnType<ZodError['format']> | null>(null)

  useEffect(() => {
    if (demoMode) {
      saveJSON('demo_symptoms', symptoms)
    }
  }, [symptoms])

  const addSymptom = async (
    symptomData: NewSymptom
  ): Promise<SymptomEntry | null> => {
    setError(null)
    const parsed = NewSymptomSchema.safeParse(symptomData)
    if (!parsed.success) {
      const formatted = parsed.error.format()
      setError(formatted)
      return null
    }
    const newSymptom: SymptomEntry = {
      id: Date.now().toString(),
      user_id: 'demo-user-id',
      ...parsed.data,
      recorded_at: new Date().toISOString(),
      created_at: new Date().toISOString()
    }
    setSymptoms(prev => [...prev, newSymptom])
    return newSymptom
  }

  return {
    symptoms,
    loading,
    error,
    addSymptom,
    refetch: () => {
      if (demoMode) {
        setSymptoms(
          loadValidatedArray<SymptomEntry>(
            'demo_symptoms',
            loadDemoSymptoms,
            NewSymptomSchema
          )
        )
      }
    }
  }
}

// Medications Hook
export function useMedications() {
  const initial = loadValidatedArray<MedicationEntry>(
    'demo_medications',
    loadDemoMedications,
    NewMedicationSchema
  );
  const [medications, setMedications] = useState<MedicationEntry[]>(initial);
  const [loading] = useState(false)
  const [error, setError] = useState<ReturnType<ZodError['format']> | null>(null)

  useEffect(() => {
    if (demoMode) {
      saveJSON('demo_medications', medications)
    }
  }, [medications])

  const addMedication = async (
    medicationData: NewMedication
  ): Promise<MedicationEntry | null> => {
    setError(null)
    const parsed = NewMedicationSchema.safeParse(medicationData)
    if (!parsed.success) {
      const formatted = parsed.error.format()
      setError(formatted)
      return null
    }
    const newMedication: MedicationEntry = {
      id: Date.now().toString(),
      user_id: 'demo-user-id',
      ...parsed.data,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    setMedications(prev => [...prev, newMedication])
    return newMedication
  }

  return {
    medications,
    loading,
    error,
    addMedication,
    refetch: () => {
      if (demoMode) {
        setMedications(
          loadValidatedArray<MedicationEntry>(
            'demo_medications',
            loadDemoMedications,
            NewMedicationSchema
          )
        )
      }
    }
  }
}

// Food Entries Hook
export function useFoodEntries() {
  const initial = loadValidatedArray<FoodEntry>(
    'demo_food',
    loadDemoFoodEntries,
    NewFoodEntrySchema
  );
  const [foodEntries, setFoodEntries] = useState<FoodEntry[]>(initial);
  const [loading] = useState(false)
  const [error, setError] = useState<ReturnType<ZodError['format']> | null>(null)

  useEffect(() => {
    if (demoMode) {
      saveJSON('demo_food', foodEntries)
    }
  }, [foodEntries])

  const addFoodEntry = async (
    foodData: NewFoodEntry
  ): Promise<FoodEntry | null> => {
    setError(null)
    const parsed = NewFoodEntrySchema.safeParse(foodData)
    if (!parsed.success) {
      const formatted = parsed.error.format()
      setError(formatted)
      return null
    }
    const newEntry: FoodEntry = {
      id: Date.now().toString(),
      user_id: 'demo-user-id',
      ...parsed.data,
      created_at: new Date().toISOString()
    }
    setFoodEntries(prev => [...prev, newEntry])
    return newEntry
  }

  return {
    foodEntries,
    loading,
    error,
    addFoodEntry,
    refetch: () => {
      if (demoMode) {
        setFoodEntries(
          loadValidatedArray<FoodEntry>(
            'demo_food',
            loadDemoFoodEntries,
            NewFoodEntrySchema
          )
        )
      }
    }
  }
}

// Device Data Hook
export function useDeviceData() {
  const [deviceData] = useState<DeviceMetric[]>(
    loadValidatedArray<DeviceMetric>(
      'demo_device_data',
      loadDemoDeviceData,
      NewDeviceMetricSchema
    )
  )
  const [loading] = useState(false)
  const [error] = useState<string | null>(null)

  return {
    deviceData,
    loading,
    error,
    refetch: () => {}
  }
}

// Medical Conditions Search Hook
export interface ConditionSearchResult {
  id: string
  name: string
  category: string
  icd10_code: string
}

export function useMedicalConditionsSearch() {
  const [searchResults, setSearchResults] = useState<ConditionSearchResult[]>([])
  const [loading, setLoading] = useState(false)

  const searchConditions = async (query: string) => {
    setLoading(true)
    // Mock search results
    const mockResults: ConditionSearchResult[] = [
      { id: 'diabetes-t2', name: 'Type 2 Diabetes Mellitus', category: 'Endocrine', icd10_code: 'E11.9' },
      { id: 'hypertension', name: 'Hypertension', category: 'Cardiovascular', icd10_code: 'I10' },
      { id: 'asthma', name: 'Asthma', category: 'Respiratory', icd10_code: 'J45.9' }
    ].filter(condition =>
      condition.name.toLowerCase().includes(query.toLowerCase())
    )
    
    setTimeout(() => {
      setSearchResults(mockResults)
      setLoading(false)
    }, 500)
  }

  return {
    searchResults,
    loading,
    searchConditions
  }
}
